import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { z } from 'zod';

import { useAuth } from '../../contexts/AuthContext';
import {
  approvalGroupsService,
  ApprovalGroupCurrent,
  ApprovalGroupMember,
} from '../../services/approvalGroupsService';
import { roleService } from '../../services/auth/roleService';
import {
  extractViewerDomain,
  isPublicViewerDomain,
} from '../../utils/partnerViewerScope';

export const approvalGroupKeys = {
  all: ['approvalGroup'] as const,
  current: () => [...approvalGroupKeys.all, 'current'] as const,
  candidates: (search: string) =>
    [...approvalGroupKeys.all, 'candidates', search] as const,
};

const emptyApprovalGroup: ApprovalGroupCurrent = {
  enabled: false,
  members: [],
  awaitingApprovalCount: 0,
};

function canUseApprovalsConfig(
  user: {
    email?: string;
    userType?: string;
  } | null,
): boolean {
  if (!roleService.isExternalUser(user ?? null)) {
    return false;
  }
  const domain = extractViewerDomain(user?.email);
  if (!domain || isPublicViewerDomain(domain)) {
    return false;
  }
  return true;
}

/**
 * Fetch the caller's Vendor Approvals Group.
 * External non-public-domain users only. Offline / error: empty group.
 */
export const useApprovalGroup = () => {
  const { isAuthenticated, user } = useAuth();
  const canFetch = isAuthenticated && canUseApprovalsConfig(user ?? null);

  const currentSchema = useMemo(
    () =>
      z.object({
        enabled: z.boolean(),
        members: z.array(
          z.object({
            userId: z.number().int().positive(),
            email: z.string(),
            name: z.string(),
          }),
        ),
        awaitingApprovalCount: z.number().int().nonnegative().optional(),
      }),
    [],
  );

  return useQuery<ApprovalGroupCurrent, Error>({
    queryKey: approvalGroupKeys.current(),
    queryFn: async () => {
      const raw = await approvalGroupsService.getCurrent();
      const parsed = currentSchema.safeParse(raw);
      if (!parsed.success) {
        return emptyApprovalGroup;
      }
      return {
        ...parsed.data,
        awaitingApprovalCount: parsed.data.awaitingApprovalCount ?? 0,
      };
    },
    enabled: canFetch,
    staleTime: 30 * 1000,
    retry: false,
  });
};

export const useApprovalGroupCandidates = (search: string) => {
  const { isAuthenticated, user } = useAuth();
  const canFetch = isAuthenticated && canUseApprovalsConfig(user ?? null);
  const trimmed = search.trim();

  const candidatesSchema = useMemo(
    () =>
      z.object({
        candidates: z.array(
          z.object({
            userId: z.number().int().positive(),
            email: z.string(),
            name: z.string(),
          }),
        ),
      }),
    [],
  );

  return useQuery({
    queryKey: approvalGroupKeys.candidates(trimmed),
    queryFn: async () => {
      const raw = await approvalGroupsService.getCandidates(
        trimmed || undefined,
      );
      return candidatesSchema.parse(raw);
    },
    enabled: canFetch && trimmed.length >= 1,
    staleTime: 15 * 1000,
    retry: false,
  });
};

export const useAddApprovalGroupMember = () => {
  const queryClient = useQueryClient();
  const memberSchema = useMemo(
    () =>
      z.object({
        userId: z.number().int().positive(),
        email: z.string(),
        name: z.string(),
      }),
    [],
  );

  return useMutation({
    mutationFn: async (userId: number): Promise<ApprovalGroupMember> => {
      const id = Number(userId);
      if (!Number.isInteger(id) || id <= 0) {
        throw new Error('userId must be a positive integer');
      }
      const raw = await approvalGroupsService.addMember(id);
      return memberSchema.parse(raw);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: approvalGroupKeys.all });
    },
  });
};

export const useRemoveApprovalGroupMember = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: number) => approvalGroupsService.removeMember(userId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: approvalGroupKeys.all });
    },
  });
};

export const useSetApprovalGroupEnabled = () => {
  const queryClient = useQueryClient();
  const currentSchema = useMemo(
    () =>
      z.object({
        enabled: z.boolean(),
        members: z.array(
          z.object({
            userId: z.number().int().positive(),
            email: z.string(),
            name: z.string(),
          }),
        ),
        awaitingApprovalCount: z.number().int().nonnegative().optional(),
      }),
    [],
  );

  return useMutation({
    mutationFn: async (enabled: boolean): Promise<ApprovalGroupCurrent> => {
      const raw = await approvalGroupsService.setEnabled(enabled);
      return currentSchema.parse(raw);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: approvalGroupKeys.all });
    },
  });
};

export { canUseApprovalsConfig };
